import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Button,
  InlineStack,
  Banner,
  BlockStack,
  Text,
  Spinner,
  Checkbox,
  Card,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  previewFolderForCart,
  FolderPreview,
  FolderPreviewCategory,
  CART_SIZE_LIMIT_BYTES,
  CART_SIZE_LIMIT_LABEL,
} from "features/document-cart/document-cart.service";
import { addFolderThunk } from "features/document-cart/documentCart.slice";
import "./document-cart.scss";

interface AddFolderToCartConfirmProps {
  categoryId: string;
  categoryName: string;
  source?: "document" | "profile";
}

function collectDescendantIds(
  categories: FolderPreviewCategory[],
  rootId: string
): string[] {
  const childrenByParent = new Map<string | null, string[]>();
  for (const cat of categories) {
    const parent = cat.parentId;
    if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
    childrenByParent.get(parent)!.push(cat.id);
  }
  const result: string[] = [];
  const queue = [rootId];
  while (queue.length) {
    const current = queue.shift()!;
    result.push(current);
    for (const childId of childrenByParent.get(current) || []) {
      queue.push(childId);
    }
  }
  return result;
}

const AddFolderToCartConfirm: React.FC<AddFolderToCartConfirmProps> = ({
  categoryId,
  categoryName,
  source = "document",
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [preview, setPreview] = useState<FolderPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!companyId || !categoryId) return;
      setLoadingPreview(true);
      setError(null);
      try {
        const data = await previewFolderForCart(
          companyId,
          categoryId,
          true,
          source
        );
        if (cancelled) return;
        setPreview(data);
        // Pre-select every accessible category the user can see.
        const initial: Record<string, boolean> = {};
        for (const cat of data.categories || []) {
          initial[cat.id] = true;
        }
        setSelectedIds(initial);
      } catch {
        if (!cancelled) setError(t("document_cart_folder_preview_failed"));
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [companyId, categoryId, source, t]);

  const categories: FolderPreviewCategory[] = preview?.categories || [];

  const selectedCategories = useMemo(
    () => categories.filter((c) => selectedIds[c.id]),
    [categories, selectedIds]
  );

  const selectedNewCount = useMemo(
    () => selectedCategories.reduce((sum, c) => sum + c.newFileCount, 0),
    [selectedCategories]
  );

  const selectedFileCount = useMemo(
    () => selectedCategories.reduce((sum, c) => sum + c.fileCount, 0),
    [selectedCategories]
  );

  const wouldExceedCap = useMemo(() => {
    if (!preview) return false;
    return preview.currentCartCount + selectedNewCount > preview.maxCartItems;
  }, [preview, selectedNewCount]);

  const selectedNewSize = useMemo(
    () => selectedCategories.reduce((sum, c) => sum + (c.newSizeBytes || 0), 0),
    [selectedCategories]
  );

  const wouldExceedSizeCap = useMemo(() => {
    if (!preview) return false;
    const currentCartBytes = preview.currentCartBytes || 0;
    const maxCartBytes = preview.maxCartBytes || CART_SIZE_LIMIT_BYTES;
    return currentCartBytes + selectedNewSize > maxCartBytes;
  }, [preview, selectedNewSize]);

  const allSelected =
    categories.length > 0 && categories.every((c) => selectedIds[c.id]);
  const noneSelected =
    categories.length === 0 || categories.every((c) => !selectedIds[c.id]);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const toggleCategory = (id: string, checked: boolean) => {
    const affected = collectDescendantIds(categories, id);
    setSelectedIds((prev) => {
      const next = { ...prev };
      for (const catId of affected) next[catId] = checked;
      return next;
    });
  };

  const selectAll = () => {
    const next: Record<string, boolean> = {};
    for (const cat of categories) next[cat.id] = true;
    setSelectedIds(next);
  };

  const deselectAll = () => {
    const next: Record<string, boolean> = {};
    for (const cat of categories) next[cat.id] = false;
    setSelectedIds(next);
  };

  const handleConfirm = async () => {
    if (!companyId || !categoryId) return;
    const categoryIds = selectedCategories.map((c) => c.id);
    if (!categoryIds.length) {
      setError(t("document_cart_folder_none_selected"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await dispatch(
        addFolderThunk({
          companyId,
          categoryId,
          recursive: true,
          categoryIds,
          source,
        })
      ).unwrap();

      if (result.truncatedBySize) {
        toast.success(
          t("document_cart_folder_added_size_partial", { count: result.added, limit: CART_SIZE_LIMIT_LABEL })
        );
      } else if (result.alreadyInCart || result.added === 0) {
        toast.message(t("document_cart_folder_already_in"));
      } else if (result.truncated) {
        toast.success(
          t("document_cart_folder_added_partial", { count: result.added })
        );
      } else {
        toast.success(
          t("document_cart_folder_added", { count: result.added })
        );
      }
      closeModal();
    } catch (code) {
      if (String(code).includes("EMPTY_FOLDER")) {
        setError(t("document_cart_folder_empty"));
      } else if (String(code).includes("CART_FULL")) {
        setError(t("document_cart_full"));
      } else if (
        String(code).includes("CART_TOO_LARGE") ||
        String(code).includes("ZIP_TOO_LARGE")
      ) {
        setError(t("document_cart_too_large", { limit: CART_SIZE_LIMIT_LABEL }));
      } else if (String(code).includes("FILE_TOO_LARGE")) {
        setError(t("document_cart_file_too_large", { limit: CART_SIZE_LIMIT_LABEL }));
      } else {
        setError(t("document_cart_add_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canAdd =
    !!preview &&
    selectedNewCount > 0 &&
    !wouldExceedCap &&
    !wouldExceedSizeCap &&
    !loadingPreview &&
    !noneSelected;

  return (
    <BlockStack gap="400">
      {error && <Banner tone="critical">{error}</Banner>}

      <Text as="h2" variant="headingMd" alignment="center">
        {t("document_cart_add_folder_title", { name: categoryName })}
      </Text>

      {loadingPreview ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
          <Spinner size="small" />
        </div>
      ) : preview ? (
        <BlockStack gap="300">
          <Text as="p" alignment="center">
            {t("document_cart_add_folder_body", {
              count: selectedFileCount,
              newCount: selectedNewCount,
            })}
          </Text>
          <Text as="p" tone="subdued" alignment="center" variant="bodySm">
            {t("document_cart_add_folder_select_hint")}
          </Text>

          {categories.length > 0 ? (
            <div className="document-cart-folder-picker">
              <div className="document-cart-folder-picker__toolbar">
                <Button
                  variant="plain"
                  onClick={selectAll}
                  disabled={allSelected || submitting}
                >
                  {t("document_cart_folder_select_all")}
                </Button>
                <Button
                  variant="plain"
                  onClick={deselectAll}
                  disabled={noneSelected || submitting}
                >
                  {t("document_cart_folder_deselect_all")}
                </Button>
              </div>
              <ul className="document-cart-folder-picker__list">
                {categories.map((cat) => (
                  <li
                    key={cat.id}
                    className="document-cart-folder-picker__item"
                    style={{ paddingLeft: 8 + cat.depth * 16 }}
                  >
                     <Checkbox
                      label={cat.title}
                      helpText={
                        cat.fileCount > 0
                          ? [
                              t("document_cart_folder_file_count", {
                                count: cat.fileCount,
                              }),
                              cat.alreadyInCartCount > 0
                                ? t("document_cart_folder_already_short", {
                                    count: cat.alreadyInCartCount,
                                  })
                                : null,
                              cat.newSizeBytes && cat.newSizeBytes > 0
                                ? `+${(cat.newSizeBytes / (1024 * 1024)).toFixed(2)} MB`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")
                          : t("document_cart_folder_no_direct_files")
                      }
                      checked={!!selectedIds[cat.id]}
                      disabled={submitting}
                      onChange={(checked) => toggleCategory(cat.id, checked)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Banner tone="warning">{t("document_cart_folder_empty")}</Banner>
          )}

          {(() => {
            const currentSizeMB = ((preview.currentCartBytes || 0) / (1024 * 1024)).toFixed(2);
            const folderSizeMB = (selectedNewSize / (1024 * 1024)).toFixed(2);
            const totalSizeMB = (((preview.currentCartBytes || 0) + selectedNewSize) / (1024 * 1024)).toFixed(2);
            const maxMB = ((preview.maxCartBytes || CART_SIZE_LIMIT_BYTES) / (1024 * 1024)).toFixed(0);
            
            return (
              <Card>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {t("document_cart_size_summary")}
                  </Text>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">
                      {t("document_cart_current_size")}
                    </Text>
                    <Text as="span" fontWeight="medium">{currentSizeMB} MB</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">
                      {t("document_cart_adding_size")}
                    </Text>
                    <Text as="span" fontWeight="medium">{folderSizeMB} MB</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">
                      {t("document_cart_estimated_total_size")}
                    </Text>
                    <Text as="span" fontWeight="bold" tone={wouldExceedSizeCap ? "critical" : "success"}>
                      {t("document_cart_total_size_limit", {
                        total: totalSizeMB,
                        limit: maxMB,
                      })}
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Card>
            );
          })()}

          {preview.alreadyInCartCount > 0 && selectedNewCount > 0 && (
            <Banner tone="info">
              {t("document_cart_folder_some_already", {
                count: preview.alreadyInCartCount,
              })}
            </Banner>
          )}
          {wouldExceedCap && (
            <Banner tone="warning">
              {t("document_cart_folder_would_exceed", {
                max: preview.maxCartItems,
              })}
            </Banner>
          )}
          {wouldExceedSizeCap && (
            <Banner tone="critical">
              {t("document_cart_too_large", { limit: CART_SIZE_LIMIT_LABEL })}
            </Banner>
          )}
          {preview.fileCount === 0 && categories.length > 0 && (
            <Banner tone="warning">{t("document_cart_folder_empty")}</Banner>
          )}
          {noneSelected && categories.length > 0 && (
            <Banner tone="warning">
              {t("document_cart_folder_none_selected")}
            </Banner>
          )}
        </BlockStack>
      ) : null}

      <InlineStack align="space-between">
        <Button onClick={handleConfirm} loading={submitting} disabled={!canAdd}>
          {t("document_cart_add_folder_confirm")}
        </Button>
        <Button onClick={closeModal} disabled={submitting}>
          {t("cancel")}
        </Button>
      </InlineStack>
    </BlockStack>
  );
};

export default AddFolderToCartConfirm;
